import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Bug, Lightbulb, Sparkles, LifeBuoy, Mail } from "lucide-react";

const SUPPORT_EMAIL = "info@airava-workflow.com";

const COPY = {
  en: {
    overline: "Help & Ideas",
    title: "Contact & Feedback",
    heading: "Need help or have an idea for Airava?",
    body:
      "If you experience a problem, need support, or have a suggestion for a new feature or improvement, write to us. If your request is aligned with the Airava project and can improve the platform for its users, we will evaluate it and, where appropriate, develop it.",
    cta: "Contact us",
    emailLabel: "Write to us at",
    subject: "Airava – Support / Feedback Request",
    cards: [
      { icon: Bug, title: "Report a Problem", desc: "Bugs, errors or unexpected behavior." },
      { icon: Lightbulb, title: "Request a Feature", desc: "New functionality or implementations." },
      { icon: Sparkles, title: "Suggest an Improvement", desc: "UX, workflow or process improvements." },
      { icon: LifeBuoy, title: "General Support", desc: "General assistance requests." },
    ],
  },
  it: {
    overline: "Aiuto & Idee",
    title: "Contatti & Feedback",
    heading: "Hai bisogno di aiuto o hai un'idea per migliorare Airava?",
    body:
      "Se riscontri un problema, hai bisogno di supporto oppure vuoi proporre una modifica, una nuova funzionalità o un miglioramento, scrivici. Se la richiesta sarà coerente con il progetto Airava e potrà portare valore alla piattaforma e ai suoi utenti, la valuteremo e, dove opportuno, la svilupperemo.",
    cta: "Scrivici",
    emailLabel: "Scrivici a",
    subject: "Airava – Support / Feedback Request",
    cards: [
      { icon: Bug, title: "Segnala un problema", desc: "Bug, errori o comportamenti inattesi." },
      { icon: Lightbulb, title: "Richiedi una funzionalità", desc: "Nuove funzionalità o implementazioni." },
      { icon: Sparkles, title: "Suggerisci un miglioramento", desc: "Miglioramenti di UX, workflow o processi." },
      { icon: LifeBuoy, title: "Supporto generale", desc: "Richieste generiche di assistenza." },
    ],
  },
};

export default function ContactFeedbackPage() {
  const { user } = useAuth();
  const t = COPY[user?.language === "it" ? "it" : "en"];

  // Minimal, non-sensitive technical context (no passwords/secrets/project/document data).
  const mailto = useMemo(() => {
    const body = [
      "",
      "",
      "— — —",
      `Name: ${user?.name || ""}`,
      `Email: ${user?.email || ""}`,
      `Page: ${typeof window !== "undefined" ? window.location.href : ""}`,
    ].join("\n");
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t.subject)}&body=${encodeURIComponent(body)}`;
  }, [user?.name, user?.email, t.subject]);

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8 max-w-4xl" data-testid="contact-feedback-page">
      <p className="overline">{t.overline}</p>
      <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="contact-title">{t.title}</h1>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6 sm:p-8">
        <h2 className="text-lg font-semibold text-slate-900" data-testid="contact-heading">{t.heading}</h2>
        <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed max-w-2xl" data-testid="contact-body">{t.body}</p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {t.cards.map((c) => (
            <div key={c.title} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid={`contact-card-${c.title.split(" ")[0].toLowerCase()}`}>
              <div className="h-9 w-9 shrink-0 rounded-lg bg-slate-900 text-white grid place-items-center">
                <c.icon className="h-4 w-4" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-sm">{c.title}</h3>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{c.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
          <Button asChild className="bg-[#2563EB] hover:bg-[#1d4ed8] h-11 px-6 w-full sm:w-auto" data-testid="contact-cta">
            <a href={mailto}><Mail className="h-4 w-4 mr-2" />{t.cta}</a>
          </Button>
          <div className="text-sm text-slate-500">
            <span className="mr-1.5">{t.emailLabel}</span>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium text-[#2563EB] hover:underline" data-testid="contact-email">{SUPPORT_EMAIL}</a>
          </div>
        </div>
      </div>
    </div>
  );
}
